import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import SVG from "react-inlinesvg";
import TextArea from "react-textarea-autosize";
import { useDraggable } from "@dnd-kit/core";
import { motion, AnimatePresence } from "framer-motion";
import React, { useRef, useState, useEffect, useCallback, useMemo, memo } from "react";
import { RestorableTiptapField } from "../../../../TiptapField";
import { useStructuredOutlineStore } from "../../StructuredOutlineStore";
import DynamicInputLength from "../../../../DynamicInputLength";
import { useStoryBibleStore } from "../../../../../stores/storyBibleStore";
import { useAppStore } from "../../../../../stores/appStore";
import { useProjectStore } from "../../../../../stores/projectStore";
import GoButton from "../../../../GoButton";
import FieldLoading from "../../../../CustomField/FieldLoading";
import { extractTextFromTipTap } from "../../../../../../../src/shared/schemas/CharacterSchema";
import Auth from "../../../../../Auth";
import { themeVariables } from "../../../../../themeVariables";
import classNames from "classnames";
import { useSeriesStore } from "../../../../../stores/seriesStore";
import { TOAST_MESSAGE_ERROR_MAP } from "../../../../../../../src/shared/ToastMessageErrorMap";
import { useToast } from "../../../../ToastWrapper";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { debounce } from "lodash";
import { ChapterTensePOVBar } from "./ChapterTensePOVBar";
import { useOutlinePOVTense } from "../../useOutlinePOVTense";
import ThreeDotsSelect from "src/sudokit/components/SudoSelect/ThreeDotsSelect";
import IconButton from "src/components/IconButton";
import AnimatedWrapper from "src/components/AnimatedWrapper";
import { AppEventPublisher, StoryBibleGenerateType, StoryBibleAction, StoryBibleType } from "../../../../../AppEventPublisher";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { PROSE_MODES } from "../../..";
import { cleanTiptapDiffs, diffToTiptap } from "src/utils/diffToTiptap";
import { cn } from "src/lib/cn.js";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { useStableCallback } from "src/hooks/useStableCallback";
import { useCrossAppSignalStore, getChapterCTASignal } from "src/stores/crossAppSignalStore";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isStoryBibleCtaBlockingRetrigger } from "src/components/PersistentChat/storyBibleCta";

const COLLAPSE_ICONS_BREAKPOINT = 600;

const ChapterContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 22px;
  background-color: var(--simplified-sb-background);
  box-shadow: var(--tiny-drop-shadow-head-on);
  margin: 0px 16px 0px 16px;
  padding: 6px;
  padding-left: 14px;
  container-type: inline-size;

  &:hover {
    outline: 1px solid var(--primary-cta);
    outline-offset: -1px;
  }

  .button-creating {
    background-color: ${themeVariables.menuColorHover2};
    scale: 0.98;
    transition:
      scale 0.2s,
      background-color 0.2s;
  }
`;

const ChapterTitle = styled.div`
  display: flex;
  gap: 0.5rem 1rem;
  padding-bottom: 0;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;

  svg {
    color: ${themeVariables.grey2};
  }

  span:not(.sudokit span) {
    margin-right: 10px;
    font-size: 18px;
    font-weight: 500;
  }
  [aria-hidden="true"]:not(:has(svg)) {
    min-width: 24px;
  }
  input {
    font-family: "Inter";
    background-color: transparent;
    font-size: 18px;
    font-weight: 500;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 6px 6px;
    color: ${themeVariables.grey0};
    &:hover {
      border: 1px solid var(--primary-cta);
    }
  }

  .rotated-chevron {
    transform: rotate(-90deg);
  }
  .chevron {
    cursor: pointer;
    outline: none;
    width: 20px;
    height: 20px;
    path {
      fill: ${themeVariables.grey2};
    }
  }
  .chevron:focus-visible {
    background-color: var(--grey-4);
    padding: 2px;
    border-radius: 4px;
  }

  input:focus {
    border: 1px solid ${themeVariables.primaryCTA};
  }
  padding-right: 2px;
`;

const ChapterTitleMiddle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-grow: 1;
  gap: 10px;
  .rotated-chevron {
    transform: rotate(-90deg);
  }
  .chevron {
    cursor: pointer;
    outline: none;
  }
  .chevron:focus-visible {
    background-color: var(--grey-4);
    padding: 2px;
    border-radius: 4px;
  }
  svg {
    path {
      fill: ${themeVariables.grey0};
    }
  }
  .chapter-title-diff {
    color: ${themeVariables.primaryCTAHighContrast};
    font-weight: 600;
  }
`;

const ChapterSummary = styled.div`
  margin-right: 8px;
  margin-bottom: 8px;
  margin-top: 0;
  overflow: hidden;
  .chapter-summary-border {
    display: flex;
    flex-direction: column;

    border-radius: 5px;
    background: ${themeVariables.documentBackground};
    padding: 0px;
    font-size: 16px;
    font-family: "Inter";
    border: 1px solid ${themeVariables.grey3};
    box-shadow: ${themeVariables.tinyDropShadow};

    &:focus-within {
      border: 1px solid ${themeVariables.primaryCTA};
    }
    div {
      background: transparent;
    }
    .ProseMirror {
      color: ${themeVariables.grey0};
    }
  }
`;

const RewriteDirectionsContainer = styled.div`
  min-width: 440px;
  border-radius: 20px;
  background-color: ${themeVariables.menuColorFullscreen} !important;
  box-shadow: ${themeVariables.dropShadow1525};
  display: flex;
  flex-direction: row;
  align-items: top;
  justify-content: space-between;
  padding: 10px;
  position: absolute;
  z-index: 9999;
`;

const DirectionsLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 9px;
  padding-top: 8px;
  padding-bottom: 9px;
  padding-right: 10px;
  flex-grow: 1;
  text-align: left;
  p {
    color: ${themeVariables.grey0};
    margin-bottom: 10px;
  }
  .text-area {
    border: none;
    min-height: 40px;
    padding: 0px;
    margin: 0px;
    resize: none;
    font-family: "Inter";
    font-size: 16px;
    background-color: ${themeVariables.menuColorFullscreen};
    color: ${themeVariables.grey0};
  }
`;

// Scroll-aware portal popover component
function PortalPopover({ anchorRef, open, children, offset = 8, onClose }) {
  const popoverRef = useRef();
  const [style, setStyle] = useState({ top: 0, left: 0, position: "fixed" });

  useEffect(() => {
    function updatePosition() {
      if (anchorRef.current && popoverRef.current) {
        const REWRITE_POPOVER_WIDTH = 450;
        const rect = anchorRef.current.getBoundingClientRect();
        setStyle({
          position: "fixed",
          top: rect.bottom + window.scrollY,
          left: rect.right - REWRITE_POPOVER_WIDTH - offset + window.scrollX,
          zIndex: 9999,
        });
      }
    }
    if (open) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorRef, open, offset]);

  useEffect(() => {
    function handleClick(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose?.();
      }
    }

    if (open) {
      document.addEventListener("click", handleClick);
      document.addEventListener("menuInteraction", handleClick);
    } else {
      return;
    }
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("menuInteraction", handleClick);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
}

function Chapter({ projectId, outlineProjectId = projectId, chapter, draggingItemId, readOnly, isFirstChapter, syncing, onSummaryBlur }) {
  const userId = useAppStore((state) => state.user?.uid);
  const features = useSudowriteStore((state) => state.getFeatureFlags)();
  const { hasAccessToPOVAndTenseBeta } = features || {};
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const ctaShimmer = useCrossAppSignalStore((state) => state.signals[getChapterCTASignal(chapter.id)]);
  const globalCtaActive = usePersistentChatStore((state) =>
    isStoryBibleCtaBlockingRetrigger(state.ctaAction),
  );

  const [newDocumentId, setNewDocumentId] = useState(null);
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [showRewriteDirections, setShowRewriteDirections] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [focussed, setFocussed] = useState(false);
  const [chapterDirections, setChapterDirections] = useState(chapter.directions);
  const [updating, setUpdating] = useState(false);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({ id: chapter.id, disabled: readOnly });
  const nameInputRef = useRef();
  const directionsContainerRef = useRef();
  const textareaRef = useRef();
  const scenes = useStoryBibleStore((state) => state.scenes);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const storyBibleSaved = useStoryBibleStore((state) => state.saved);
  const createStructureOutlineScene = useStoryBibleStore((state) => state.createStructureOutlineScene);
  const setSceneId = useStoryBibleStore((state) => state.setSceneId);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const getDraftDefaults = useSudowriteStore((state) => state.getDraftDefaults);
  const synopsis = useStoryBibleStore((state) => state.synopsis);
  const genre = useStoryBibleStore((state) => state.genre);
  const updateItemName = useStructuredOutlineStore((state) => state.updateItemName);
  const updateOutlineChapterSummary = useStructuredOutlineStore((state) => state.updateOutlineChapterSummary);
  const getPromptRepresentation = useStructuredOutlineStore((state) => state.getPromptRepresentation);
  const deleteItem = useStructuredOutlineStore((state) => state.deleteItem);
  const projectContents = useProjectStore((state) => state.contents);
  const setDocumentId = useProjectStore((state) => state.setDocumentId);
  const chapterDiffs = useStructuredOutlineStore((state) => state.chapterDiffs);
  const clearChapterDiff = useStructuredOutlineStore((state) => state.clearChapterDiff);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const project = useProjectStore((state) => state.project);
  const restoring = useStructuredOutlineStore((state) => state.restoring);
  const setItemCollapse = useStructuredOutlineStore((state) => state.setItemCollapse);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const subscription = useSudowriteStore((state) => state.subscription);
  const { isMultiplePOV } = useOutlinePOVTense(outlineProjectId);
  const multiplePOV = isMultiplePOV.value;
  const handleDelete = () => {
    AppEventPublisher.storyBible.outline.delete("chapter");
    return deleteItem(chapter.id);
  };
  const isDragging = draggingItemId === chapter.id;
  const dragCursor = isDragging ? "grabbing" : "grab";
  const { setToast } = useToast();
  const rewriteButtonRef = useRef();
  const chapterContainerRef = useRef();
  const [selectValue, setSelectValue] = useState(null);
  const [hoverButtonsVisible, setHoverButtonsVisible] = useState(false);

  const projectToUpdate = selectedProjects.outline?.projectId || projectId;
  const centerWidth = columnWidths.center.current;
  const displayPovBottom = centerWidth < 1060;
  const collapseIcons = centerWidth < COLLAPSE_ICONS_BREAKPOINT;

  const handleRewriteOutlineChapter = useCallback(async () => {
    try {
      setRewriting(true);
      setShowRewriteDirections(false);
      const body = {
        projectId: projectToUpdate,
        guidance: chapterDirections,
        chapterName: chapter.name,
        fullOutline: getPromptRepresentation(),
        chapterId: chapter.id,
        outlineItems: useStructuredOutlineStore.getState().outline?.content?.items,
      };
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/outline/rewrite-outline-chapter", body);
      if (result.success) {
        AppEventPublisher.storyBible.generate(StoryBibleGenerateType.RewriteOutlineChapter);
        updateOutlineChapterSummary(chapter.id, result.chapter.summary, project);
        setChapterDirections("");
      } else {
        throw new Error("An unspecified error has occurred.");
      }
    } catch (e) {
      Sentry.captureException(e, { tags: { feature: "structured_outline_rewrite_chapter" } });
      setToast("Unable to rewrite chapter.  Please try again.");
    } finally {
      setRewriting(false);
    }
  }, [chapter.id, chapter.name, chapterDirections, getPromptRepresentation, project, projectToUpdate, setToast, updateOutlineChapterSummary]);

  const handleTextKeyboardSubmit = useCallback(
    async (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        await handleRewriteOutlineChapter();
      }
    },
    [handleRewriteOutlineChapter],
  );

  const handleUpdateFromDocument = useCallback(
    async (documentId) => {
      AppEventPublisher.storyBible.generate(StoryBibleGenerateType.UpdateOutlineChapterFromDocument);
      try {
        setUpdating(true);
        const body = {
          projectId,
          documentId,
          synopsis,
          genre,
          currentSummary: chapter.summary,
          chapterName: chapter.name,
          fullOutline: getPromptRepresentation(),
        };
        const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/outline/update-chapter-from-document", body);
        if (result.success) {
          updateOutlineChapterSummary(chapter.id, result.chapter.summary, project);
          setChapterDirections("");
        } else {
          throw new Error("An unspecified error has occurred.");
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "structured_outline_update_from_document" } });
        const { error } = await err.response.json();
        setToast(TOAST_MESSAGE_ERROR_MAP[error] || "Unable to update. Please try again.");
      } finally {
        setUpdating(false);
      }
    },
    [chapter.id, chapter.name, chapter.summary, genre, getPromptRepresentation, project, projectId, setToast, synopsis, updateOutlineChapterSummary],
  );

  const handleAdd = useCallback(
    async (source) => {
      if (creatingDocument) return;
      setCreatingDocument(true);
      AppEventPublisher.storyBible.outline.addChapterDocument(source);
      try {
        const chapters = buildChapterOptions();
        const previousDocumentId = subscription?.hasAccessToChapterGlueBeta
          ? await project.getPreviousDocumentIdFromChapter({
            chapterId: chapter.id,
            chapters,
            scenes,
            userId: subscription.id,
          })
          : null;
        const result = await project.createDocument({
          title: chapter.name,
          previousDocumentId,
          ...(hasAccessToPOVAndTenseBeta ? { povTenseId: chapter.povTenseId } : {}),
        });
        if (!result?.document?.id) {
          setCreatingDocument(false);
          setToast("Unable to create document.  Please try again.");
          return;
        }
        const newSceneId = createStructureOutlineScene(result.document.id, chapter.id);

        const { modelKey, creativity } = getDraftDefaults(project?.id);
        if (modelKey || typeof creativity === "number") {
          setScenes((draft) => {
            if (!draft[newSceneId]) return;
            if (modelKey && PROSE_MODES?.[modelKey]) {
              draft[newSceneId].proseMode = modelKey;
            }
            if (typeof creativity === "number") {
              draft[newSceneId].creativityLevel = creativity;
            }
          });
        }
        setNewDocumentId(result.document.id);
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "structured_outline_create_document_chapter" } });
        setToast("Unable to create document.  Please try again.");
        setCreatingDocument(false);
      }
    },
    [
      buildChapterOptions,
      chapter.id,
      chapter.name,
      chapter.povTenseId,
      createStructureOutlineScene,
      creatingDocument,
      getDraftDefaults,
      hasAccessToPOVAndTenseBeta,
      project,
      scenes,
      setScenes,
      setToast,
      subscription?.hasAccessToChapterGlueBeta,
      subscription.id,
    ],
  );

  useEffect(() => {
    // This is used to wait until the story bible is saved before nagivating to the new document.
    try {
      if (!newDocumentId) return;
      if (!storyBibleSaved) return;
      const newScene = Object.values(scenes).find((i) => i?.contents?.id === newDocumentId);
      if (newScene) {
        // Apply defaults if they weren't set yet
        try {
          const { modelKey, creativity } = getDraftDefaults(project?.id);
          if ((modelKey && !newScene.proseMode) || (typeof creativity === "number" && newScene.creativityLevel === undefined)) {
            setScenes((draft) => {
              const sceneRef = draft[newScene.id];
              if (!sceneRef) return;
              if (modelKey && PROSE_MODES?.[modelKey] && !sceneRef.proseMode) {
                sceneRef.proseMode = modelKey;
              }
              if (typeof creativity === "number" && sceneRef.creativityLevel === undefined) {
                sceneRef.creativityLevel = creativity;
              }
            });
          }
        } catch (err) {
          Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "chapter_apply_defaults" } });
        }
        setSceneId(newScene.id);
        setDocumentId(newDocumentId);
        subscription?.hasAccessToChapterGlueBeta ? setProseGeneratorActive(false) : setProseGeneratorActive(true);
        setCreatingDocument(false);
        setNewDocumentId(null);
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { projectId: project?.id || "unknown", feature: "structured_outline_create_document_navigation_chapter" } });
      setToast("Unable to create document.  Please try again.");
    }
  }, [newDocumentId, scenes, setDocumentId, setProseGeneratorActive, setSceneId, setCreatingDocument, storyBibleSaved]);

  const fillInProps = useMemo(
    () => ({
      icon: (
        <SVG
          src="/assets/AI-sparkle.svg"
          aria-hidden="true"
          height={24}
          width={24}
        />
      ),
      label: "Generate",
      iconSrc: "/assets/AI-sparkle.svg",
      loadingText: "Generating...",
    }),
    []
  );

  const regenerateProps = useMemo(
    () => ({
      icon: (
        <SVG
          src="/assets/rewrite-icon.svg"
          aria-hidden="true"
          height={24}
          width={24}
        />
      ),
      label: "Rewrite",
      iconSrc: "/assets/rewrite-icon.svg",
      loadingText: "Rewriting...",
    }),
    []
  );

  const debouncedUpdateOutlineChapterSummary = debounce((value) => updateOutlineChapterSummary(chapter.id, value, project), 500);

  const isEmpty = Boolean(!extractTextFromTipTap(chapter.summary).trim());
  const traitRegenProps = useMemo(() => (isEmpty ? fillInProps : regenerateProps), [isEmpty, fillInProps, regenerateProps]);
  const connectedScenes = useMemo(
    () =>
      Object.values(scenes)
        .filter((scene) => scene.itemId === chapter.id)
        .reduce((acc, scene) => {
          if (scene.contents?.id) {
            const document = projectContents.find((c) => c.id === scene.contents.id);
            if (document) {
              acc.push({ ...scene, documentTitle: document.title });
            }
          }
          return acc;
        }, []),
    [chapter.id, projectContents, scenes],
  );

  // Check if there's a diff for this chapter
  const chapterDiff = chapterDiffs[chapter.id];
  const hasDiff = !readOnly && chapterDiff && chapterDiff.oldContent && chapterDiff.newContent;

  const tiptapValue = useMemo(() => {
    if (hasDiff) {
      const diffResult = diffToTiptap(chapterDiff.oldContent.summary, chapterDiff.newContent.summary, {
        normalizeParagraphSpacing: {
          trimEdgeEmptyLines: true,
        },
      });
      return diffResult;
    }
    return chapter.summary;
  }, [hasDiff, chapterDiff, chapter.summary, chapter.id]);

  const hasNameDiff =
    hasDiff &&
    chapterDiff?.newContent?.name &&
    chapterDiff.newContent.name !== (chapterDiff.oldContent?.name ?? chapterDiff.newContent.name);

  const isLoadingField = useMemo(
    () => rewriting || updating || (ctaShimmer && !hasDiff),
    [rewriting, updating, ctaShimmer, hasDiff],
  );
  const actuallyCollapsed = useMemo(() => {
    return chapter.collapsed && !readOnly;
  }, [chapter.collapsed, readOnly]);
  const motionProps = useMemo(
    () =>
      readOnly
        ? {}
        : {
          //
          initial: { opacity: 0, height: 0 },
          animate: { opacity: 1, height: "auto" },
          exit: { opacity: 0, height: 0 },
          transition: { duration: 0.25, ease: "easeInOut" },
        },
    [readOnly],
  );
  const showCreateChapter = useMemo(() => !project.seriesId || selectedProjects.outline?.projectId === project.id, [project.seriesId, selectedProjects.outline?.projectId, project.id]);

  const handleShowRewriteDirections = useCallback(() => {
    setShowRewriteDirections(true);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 20);
  }, []);

  const handleToggleCollapse = useCallback(() => {
    if (readOnly) return;
    setItemCollapse(chapter.id, !chapter.collapsed);
  }, [chapter.collapsed, chapter.id, readOnly, setItemCollapse]);

  const handleChapterMenuOpenChange = useStableCallback((open) => {
    if (open) {
      setShowRewriteDirections(false);
    }
  });

  const handleChapterMenuChange = useStableCallback((option) => {
    if (option === "Rename") {
      setTimeout(() => {
        nameInputRef.current.select();
      }, 20);
    } else if (option === "Rewrite" || option === "Generate") {
      if (hasAccessToAgenticChat) {
        if (!globalCtaActive) {
          emitLayoutEvent("trigger_cta_chat", {
            type: option === "Generate" ? "generate_chapter" : "rewrite_chapter",
            targetSurface: "story_bible_outline_summary",
            targetId: chapter.id,
            targetName: chapter.name,
            displayTitle: option,
            userMessage: `${option} ${chapter.name}`,
            cannedResponse: "Ok, let's do it!",
          });
        }
      } else {
        handleShowRewriteDirections(true);
      }
    } else if (option === "Delete") {
      handleDelete();
    } else if (option === "Create Document") {
      handleAdd("menu");
    } else if (connectedScenes.length > 0) {
      const optionParts = option.split(" ");
      if (optionParts[0] === "Update" && optionParts[1] === "document") {
        const sceneId = optionParts[2];
        const selectedScene = connectedScenes.find((scene) => scene.id === sceneId);
        if (selectedScene?.contents?.id) {
          AppEventPublisher.storyBible.action(StoryBibleType.outline, StoryBibleAction.NavigateToLinkedDocument);
          setDocumentId(selectedScene.contents.id);
        }
      } else if (optionParts[0] === "Update" && optionParts[1] === "outline") {
        const sceneId = optionParts[2];
        const selectedScene = connectedScenes.find((scene) => scene.id === sceneId);
        if (selectedScene?.contents?.id) {
          handleUpdateFromDocument(selectedScene.contents.id);
        }
      }
    }
    setSelectValue(null);
  });

  return (
    <ChapterContainer
      id={`story-bible-outline-chapter-${chapter.id}`}
      ref={(el) => {
        setNodeRef(el);
        chapterContainerRef.current = el;
      }}
      onPointerEnter={() => {
        setHoverButtonsVisible(true);
      }}
      onPointerLeave={() => {
        setHoverButtonsVisible(false);
      }}
      onFocus={() => {
        setHoverButtonsVisible(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setHoverButtonsVisible(false);
        }
      }}
    >
      <ChapterTitle className={"flex-row items-start"}>
        <div className="flex items-center flex-grow">
          {!readOnly && (
            <AnimatedWrapper
              isVisible={hoverButtonsVisible}
              applyToChildren={false}
            >
              <SVG
                ref={setActivatorNodeRef}
                {...listeners}
                {...attributes}
                style={{ cursor: dragCursor }}
                src="/assets/grabber.svg"
                height={24}
                width={24}
              />
            </AnimatedWrapper>
          )}
          {!readOnly && (
            <SVG
              src="/assets/chevron-down.svg"
              tabIndex="0"
              role="button"
              aria-expanded={!actuallyCollapsed}
              className={classNames("chevron", { "rotated-chevron": actuallyCollapsed })}
              alt="disclosure arrow"
              onClick={handleToggleCollapse}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggleCollapse();
                }
              }}
              aria-label={`${actuallyCollapsed ? "Expand" : "Collapse"} outline chapter summary`}
            />
          )}
          <ChapterTitleMiddle>
            <DynamicInputLength
              ref={nameInputRef}
              value={chapter.name}
              resizeTrigger={chapter.name}
              onChange={(e) => {
                if (hasDiff) {
                  clearChapterDiff(chapter.id);
                }
                updateItemName(chapter.id, e.target.value);
              }}
              onFocus={() => {
                if (hasDiff) {
                  clearChapterDiff(chapter.id);
                }
              }}
              placeholder="Chapter Name"
              disabled={readOnly || syncing}
              maxLength={100}
              className={cn("transition-opacity", syncing && "opacity-50", { "chapter-title-diff": hasNameDiff })}
            />
            {hasAccessToPOVAndTenseBeta && multiplePOV && !displayPovBottom && (
              <ChapterTensePOVBar
                chapter={chapter}
                projectId={projectId}
                outlineProjectId={outlineProjectId}
                disabled={readOnly || syncing}
                allowWrap={false}
              />
            )}
          </ChapterTitleMiddle>
        </div>
        <div className="flex items-center flex-none gap-2">
          {showCreateChapter && !collapseIcons && (
            <AnimatedWrapper isVisible={hoverButtonsVisible}>
              <IconButton
                src="/assets/addDocument.svg"
                onClick={() => handleAdd("chapter-header")}
                size={36}
                iconSize={24}
                disabled={creatingDocument || readOnly || syncing}
                hoverTipText="Start Chapter"
              />
            </AnimatedWrapper>
          )}
          <div className="mr-1 flex items-center">
            <ThreeDotsSelect
              ref={rewriteButtonRef}
              value={selectValue}
              options={[
                ...(connectedScenes?.length > 0
                  ? [
                    {
                      isGroup: true,
                      options: connectedScenes.map((scene) => ({
                        value: `Update document ${scene.id}`,
                        label: scene.documentTitle,
                        icon: { src: "/assets/document.svg", label: `Update from the ${scene.documentTitle} document`, status: "default" },
                      })),
                      groupLabel: "Linked Documents:",
                    },
                  ]
                  : []),
                ...(connectedScenes?.length > 0
                  ? connectedScenes.map((scene) => {
                    return {
                      value: `Update outline ${scene.id}`,
                      label: "Update",
                      subLabel: scene.documentTitle,
                      tooltipText: `Update the outline chapter from the ${scene.documentTitle} document`,
                      icon: { src: "/assets/restart.svg", label: "Update the outline chapter from this document.", status: "default" },
                    };
                  })
                  : []),
                ...(showCreateChapter && collapseIcons
                  ? [
                    {
                      value: "Create Document",
                      label: `Start "${chapter.name}"`,
                      icon: { src: "/assets/addDocument.svg", label: `Create document for "${chapter.name}"`, status: "default" },
                    },
                  ]
                  : []),
                ...((hasAccessToAgenticChat && globalCtaActive) ? [] : [
                  isEmpty
                    ? {
                      value: "Generate",
                      label: "Generate",
                      icon: { src: "/assets/AI-sparkle.svg", label: "Generate chapter summary", status: "default" },
                    }
                    : {
                      value: "Rewrite",
                      label: "Rewrite",
                      icon: { src: "/assets/rewrite-icon.svg", label: "Rewrite chapter summary", status: "default" },
                    },
                ]),
                {
                  value: "Rename",
                  label: "Rename",
                  icon: { src: "/assets/pencil.svg", label: "Rename chapter", status: "default" },
                },
                {
                  value: "Delete",
                  label: "Delete",
                  icon: { src: "/assets/delete.svg", label: "Delete chapter", status: "danger" },
                },
              ]}
              onOpenChange={handleChapterMenuOpenChange}
              onChange={handleChapterMenuChange}
              optionAlignment="left"
              disabled={readOnly || syncing}
              className="[&&]:p-0 h-[36px] [&&]:w-[36px] p-0 m-0 min-w-0"
              align="end"
            />
          </div>
        </div>
      </ChapterTitle>
      {hasAccessToPOVAndTenseBeta && multiplePOV && displayPovBottom && (
        <div className="mb-2">
          <ChapterTensePOVBar
            chapter={chapter}
            projectId={projectId}
            outlineProjectId={outlineProjectId}
            disabled={readOnly || syncing}
            allowWrap={true}
          />
        </div>
      )}
      <PortalPopover
        anchorRef={rewriteButtonRef}
        open={showRewriteDirections}
        onClose={() => setShowRewriteDirections(false)}
        offset={8}
      >
        <RewriteDirectionsContainer>
          <div
            ref={directionsContainerRef}
            style={{ display: "flex", width: "100%" }}
          >
            <DirectionsLeftContainer>
              <p>Custom Instructions (optional)</p>
              <TextArea
                ref={textareaRef}
                className="text-area"
                placeholder={isEmpty ? "Tell Sudowrite what to write for this chapter..." : "Tell Sudowrite how to rewrite this chapter..."}
                value={chapterDirections}
                onChange={(e) => setChapterDirections(e.target.value)}
                onKeyDown={handleTextKeyboardSubmit}
              />
            </DirectionsLeftContainer>
            <GoButton onClick={() => handleRewriteOutlineChapter()} />
          </div>
        </RewriteDirectionsContainer>
      </PortalPopover>
      <AnimatePresence>
        {!actuallyCollapsed && (
          <motion.div {...motionProps}>
            <ChapterSummary>
              {!isLoadingField && (
                <motion.div
                  className="chapter-summary-border"
                  transition={{ duration: 0.2 }}
                  initial={false}
                  exit={false}
                >
                  <RestorableTiptapField
                    key={hasDiff ? `diff-${chapter.id}` : chapter.id}
                    loading={syncing}
                    value={tiptapValue}
                    placeholder="Chapter Summary"
                    onChange={(value) => {
                      if (hasDiff) {
                        // When viewing diffs, we don't want regular edits to trigger updates
                        // However, if the user intentionally clears diffs (handled elsewhere usually),
                        // we might want to accept changes.
                        // But here, if we are just viewing, any 'change' is likely due to Tiptap normalization.
                        // So we should probably ignore 'onChange' unless it's a user action that explicitly accepts diffs.

                        // But if the user *edits* the diff view, what should happen?
                        // The existing logic tried to clean diffs and save.
                        // const cleanValue = cleanTiptapDiffs(value);
                        // clearChapterDiff(chapter.id);
                        // updateOutlineChapterSummary(chapter.id, cleanValue, project);
                        // debouncedUpdateOutlineChapterSummary(cleanValue);

                        // For now, let's prevent auto-updates when showing diffs to avoid the loop.
                        // The user should use "Accept" or "Reject" actions (not yet implemented fully in UI perhaps,
                        // but editing the text directly while in diff mode is tricky).
                        // If we want to allow editing, we must ensure we don't loop.

                        // Simplest fix for the loop: Don't update if hasDiff is true.
                        return;
                      } else {
                        debouncedUpdateOutlineChapterSummary(value);
                      }
                    }}
                    restoring={restoring || rewriting}
                    readOnly={readOnly}
                    onFocus={() => {
                      if (hasDiff) {
                        const acceptedSummary = cleanTiptapDiffs(tiptapValue);
                        updateOutlineChapterSummary(chapter.id, acceptedSummary, project);
                        clearChapterDiff(chapter.id);
                      }
                      setShowRewriteDirections(false);
                      setFocussed(true);
                    }}
                    searchableId={`outline-${chapter.id}`}
                    searchableName={`Outline: ${chapter.name || "Chapter"}`}
                    onBlur={() => {
                      setTimeout(() => setFocussed(false), 100);
                      if (!readOnly && typeof onSummaryBlur === "function") {
                        onSummaryBlur();
                      }
                    }}
                    userId={userId}
                    projectId={projectId}
                  />
                  <AnimatePresence mode="popLayout">
                    {focussed && !hasDiff && !(hasAccessToAgenticChat && globalCtaActive) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <SudoButton
                          variant="tertiary"
                          leftIcon={traitRegenProps.iconSrc}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasAccessToAgenticChat) {
                              if (!globalCtaActive) {
                                emitLayoutEvent("trigger_cta_chat", {
                                  type: isEmpty ? "generate_chapter" : "rewrite_chapter",
                                  targetSurface: "story_bible_outline_summary",
                                  targetId: chapter.id,
                                  targetName: chapter.name,
                                  displayTitle: traitRegenProps.label,
                                  userMessage: `${traitRegenProps.label} ${chapter.name}`,
                                  cannedResponse: "Ok, let's do it!",
                                });
                              }
                            } else {
                              handleShowRewriteDirections();
                            }
                          }}
                          className="ml-2 mb-2"
                        >
                          {traitRegenProps.label}
                        </SudoButton>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
              {isLoadingField && (
                <FieldLoading
                  content={tiptapValue}
                  loadingText={ctaShimmer ? null : traitRegenProps.loadingText}
                  userId={userId}
                  projectId={projectId}
                />
              )}
            </ChapterSummary>
          </motion.div>
        )}
      </AnimatePresence>
    </ChapterContainer>
  );
}

export default memo(Chapter);
